import React from 'react';
import Icon from '../../../components/AppIcon';
import { parseLocalDate } from '../../../utils/dateUtils';

const CalendarGrid = ({ currentDate, events, onEventClick, viewMode }) => {
  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    let day = startOfWeek?.getDay();
    const diff = startOfWeek?.getDate() - day;
    startOfWeek?.setDate(diff);

    const days = [];
    for (let i = 0; i < 7; i++) {
      let day = new Date(startOfWeek);
      day?.setDate(startOfWeek?.getDate() + i);
      days?.push(day);
    }
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return events?.filter(event => event?.date === dateStr);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getEventTypeColor = (type) => {
    const colors = {
      applications: 'bg-blue-500',
      exams: 'bg-red-500',
      results: 'bg-green-500',
      interviews: 'bg-purple-500',
      registration: 'bg-orange-500'
    };
    return colors?.[type] || 'bg-gray-500';
  };

  const renderMonthView = () => {
    const days = getDaysInMonth(currentDate);
    const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Week day headers */}
        <div className="grid grid-cols-7 bg-muted">
          {weekDays?.map((day) => (
            <div key={day} className="p-3 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {days?.map((date, index) => {
            const dayEvents = getEventsForDate(date);
            const isCurrentDay = isToday(date);
            const isPast = isPastDate(date);

            return (
              <div
                key={index}
                className={`min-h-24 p-2 border-r border-b border-border ${
                  !date ? 'bg-muted/50' : isPast ? 'bg-muted/30' : 'bg-card hover:bg-muted/50'
                } transition-smooth`}
              >
                {date && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${
                      isCurrentDay 
                        ? 'text-primary font-bold' 
                        : isPast 
                          ? 'text-muted-foreground' 
                          : 'text-foreground'
                    }`}>
                      {date?.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayEvents?.slice(0, 2)?.map((event, eventIndex) => (
                        <button
                          key={eventIndex}
                          onClick={() => onEventClick(event)}
                          className={`w-full text-left p-1 rounded text-xs text-white truncate transition-smooth hover:opacity-80 ${
                            getEventTypeColor(event?.type)
                          }`}
                        >
                          {event?.title}
                        </button>
                      ))}
                      {dayEvents?.length > 2 && (
                        <div className="text-xs text-muted-foreground">
                          +{dayEvents?.length - 2} autres
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const days = getWeekDays(currentDate);
    const weekDays = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-7">
          {days?.map((date, index) => {
            const dayEvents = getEventsForDate(date);
            const isCurrentDay = isToday(date);
            const isPast = isPastDate(date);

            return (
              <div key={index} className="border-r border-border last:border-r-0">
                <div className={`p-3 border-b border-border text-center ${
                  isCurrentDay ? 'bg-primary/10' : 'bg-muted/50'
                }`}>
                  <div className="text-xs text-muted-foreground">{weekDays?.[index]}</div>
                  <div className={`text-lg font-medium ${
                    isCurrentDay ? 'text-primary' : isPast ? 'text-muted-foreground' : 'text-foreground'
                  }`}>
                    {date?.getDate()}
                  </div>
                </div>
                <div className="p-2 min-h-48 space-y-1">
                  {dayEvents?.map((event, eventIndex) => (
                    <button
                      key={eventIndex}
                      onClick={() => onEventClick(event)}
                      className={`w-full text-left p-2 rounded text-xs text-white transition-smooth hover:opacity-80 ${
                        getEventTypeColor(event?.type)
                      }`}
                    >
                      <div className="font-medium truncate">{event?.title}</div>
                      <div className="opacity-90">{event?.institution}</div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAgendaView = () => {
    const monthEvents = events?.filter(event => {
      const eventDate = parseLocalDate(event.date);
      return eventDate?.getMonth() === currentDate?.getMonth() &&
             eventDate?.getFullYear() === currentDate?.getFullYear();
    })?.sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

    if (monthEvents?.length === 0) {
      return (
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Aucun événement ce mois-ci
          </h3>
          <p className="text-muted-foreground">
            Il n'y a pas d'échéances prévues pour cette période.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {monthEvents?.map((event, index) => {
          const eventDate = parseLocalDate(event.date);
          const isPast = isPastDate(eventDate);
          const isUrgent = !isPast && (eventDate - new Date()) / (1000 * 60 * 60 * 24) <= 7;

          return (
            <button
              key={index}
              onClick={() => onEventClick(event)}
              className={`w-full text-left bg-card border border-border rounded-lg p-4 transition-smooth hover:shadow-interactive ${
                isPast ? 'opacity-60' : ''
              } ${isUrgent ? 'border-warning' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`w-3 h-3 rounded-full ${getEventTypeColor(event?.type)}`} />
                    <span className="text-sm font-medium text-primary">{event?.typeLabel}</span>
                    {isUrgent && (
                      <span className="px-2 py-1 bg-warning text-warning-foreground text-xs rounded-full">
                        Urgent
                      </span>
                    )}
                  </div>
                  <h4 className="font-medium text-foreground mb-1">{event?.title}</h4>
                  <p className="text-sm text-muted-foreground mb-2">{event?.institution}</p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>{eventDate?.toLocaleDateString('fr-FR')}</span>
                    <span>{event?.location}</span>
                  </div>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  if (viewMode === 'week') return renderWeekView();
  if (viewMode === 'agenda') return renderAgendaView();
  return renderMonthView();
};

export default CalendarGrid;